/**
 * 작성자 : 홍선영
 * 날짜 : 2028.08.14
 * 기능 : TuiGridSize 설정 훅
 */

/**
 * 수정자 : 박승호
 * 날짜 : 2025.12.24
 * 수정내용 : TuiGridSize 훅 코드 리팩토링
 */

/**
 * 수정자 : 홍선영
 * 수정일자 : 2026.06.25
 * 수정내용 : 모바일 가상 키보드 표시/해제(WebView adjustResize 시 뷰포트가 줄었다 늘어남)에 대응하여,
 *           컨테이너 크기 변화를 ResizeObserver로 직접 감지하는 단일 방식으로 통일.
 */

import { RefObject, useEffect, useRef, useState } from 'react';

const useTuiGridSize = (componentRef: RefObject<HTMLDivElement>) => {
  const [tuiSize, setTuiSize] = useState<{ width: number; height: number } | null>(null);
  const observedElRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  /**
   * 컨테이너 크기 변화를 ResizeObserver로 직접 감지해 그리드 크기를 측정한다.
   * - 초기 측정: observe() 시 1회 콜백으로 초기 크기 보고.
   * - 모바일 가상 키보드 표시/해제(WebView adjustResize 시 뷰포트가 줄었다 늘어남),
   *   사이드바 fold로 인한 너비 변화, 비동기 검색 로딩 중처럼 React 리렌더가 없는 구간 등
   *   원인과 무관하게 컨테이너 박스가 바뀌면 콜백이 즉시 발화하여 정확한 시점에 재측정한다.
   *   (resize/visualViewport 이벤트나 setTimeout 추측에 의존하지 않음)
   *
   * 주의: 대상 그리드를 조건부(로딩 등)로 렌더하는 화면(예: 자유게시판)은 마운트 시점에
   * componentRef.current가 null일 수 있다. 따라서 의존성 배열 없이 매 렌더마다 대상 요소를
   * 확인하여, 요소가 새로 나타나거나 교체되면 옵저버를 재부착한다.
   */
  useEffect(() => {
    const el = componentRef?.current ?? null;
    if (el === observedElRef.current) return; // 동일 요소면 재부착 불필요

    // 이전 관찰 해제
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    observedElRef.current = el;

    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        setTuiSize((prev) => (prev && prev.width === width && prev.height === height ? prev : { ...prev, width, height }));
      });
      ro.observe(el);
      resizeObserverRef.current = ro;
    }
  });

  // 언마운트 시 옵저버 정리
  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      observedElRef.current = null;
    },
    []
  );

  return { tuiWidth: tuiSize?.width, tuiHeight: tuiSize?.height };
};

export default useTuiGridSize;
